import { cn } from "@/lib/cn";
import { Button } from "./Button";
import type { Pagination } from "@/lib/usePagination";

export interface PagerProps {
  /** The object returned by usePagination(). */
  p: Pagination<unknown>;
  /** Noun for the count label, e.g. "products", "customers". Defaults to "items". */
  noun?: string;
  className?: string;
}

/**
 * Shared pagination control — "{from}–{to} of {total} {noun}" plus
 * Previous/Next, matching the Templates/Segments house style.
 *
 * Renders nothing when there is only one page, so it is safe to drop onto
 * any list regardless of size — short lists simply show no control.
 */
export function Pager({ p, noun = "items", className }: PagerProps) {
  if (p.pageCount <= 1) return null;
  return (
    <div
      className={cn(
        "mt-3 flex items-center justify-between text-xs text-text-muted",
        className,
      )}
    >
      <span className="tabular-nums">
        {p.from}–{p.to} of {p.total} {noun}
      </span>
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={p.prev} disabled={!p.canPrev}>
          Previous
        </Button>
        <Button variant="ghost" size="sm" onClick={p.next} disabled={!p.canNext}>
          Next
        </Button>
      </div>
    </div>
  );
}
